import { useMemo, useState, type ChangeEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Layout, ErrorNotice, StatusPill } from '../components/Layout';
import { api, apiUrl } from '../lib/api';
import { uploadLibraryFile } from '../lib/stored-file-upload';
import { extractOfficeText, extractPdfText } from '../lib/document-text';
import { renderPdfPages } from '../lib/pdf-preview';
import { DocumentRelationshipPanel } from '../components/DocumentRelationshipPanel';
import { SearchableTextPanel } from '../components/SearchableTextPanel';

interface Taxonomy {
  id: string;
  name: string;
  color: string;
}
interface DocumentItem {
  id: string;
  storedFileId: string;
  title: string;
  documentNumber: string | null;
  documentDate: string | null;
  category: Taxonomy | null;
  agency: Taxonomy | null;
  labels: Taxonomy[];
  status: string;
  ocrText: string;
  summaryText: string | null;
  ocrError: string | null;
  versionNumber: number;
  legalStatus: 'active' | 'amended' | 'partially_repealed' | 'repealed';
  progress: { totalPages: number; sourcePages: number; completedPages: number };
  file: { name: string; contentType: string; sizeBytes: number; provider: string };
  files?: DocumentFileItem[];
  createdAt: string;
  updatedAt: string;
  match?: { source: 'metadata' | 'original' | 'attachment'; fileId?: string } | null;
}
interface DocumentFileItem {
  id: string;
  storedFileId: string;
  displayName: string;
  includeInSearch: boolean;
  status: string;
  ocrText: string;
  ocrError: string | null;
  ocrProvider: string | null;
  ocrModel: string | null;
  estimatedCostThb: number | null;
  lastOcrAt: string | null;
  ocrRuns: Array<{
    id: string;
    provider: string;
    model: string;
    status: string;
    sourcePages: number;
    estimatedCostThb: number;
    errorMessage: string | null;
    createdAt: string;
    completedAt: string | null;
  }>;
  progress: { totalPages: number; sourcePages: number; completedPages: number };
  file: { name: string; contentType: string; sizeBytes: number; provider: string };
  createdAt: string;
  updatedAt: string;
}
interface QueueItem {
  id: string;
  file: File;
  progress: number;
  message: string;
  error?: string;
  done?: boolean;
}
interface PendingDocumentFile {
  id: string;
  file: File;
  pages: number;
  ocr: boolean;
  canOcr: boolean;
  estimate: number;
  mode: 'native' | 'pages' | 'none';
  nativeText?: string;
  status: string;
  done?: boolean;
  error?: string;
}

const statusLabels: Record<string, string> = {
  draft: 'รออ่านเอกสาร',
  ocr_queued: 'รอคิว OCR',
  ocr_processing: 'กำลัง OCR',
  needs_review: 'รอตรวจสอบ',
  ready: 'พร้อมใช้',
  ocr_failed: 'OCR ไม่สำเร็จ',
};
const officeTypes = ['docx', 'xlsx', 'pptx'];
const accepted = '.pdf,.jpg,.jpeg,.png,.webp,.docx,.xlsx,.pptx,.txt,.csv,.md';
const baseName = (name: string) => name.replace(/\.[^.]+$/, '');

export function DocumentLibraryPage() {
  const client = useQueryClient();
  const [query, setQuery] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [agencyId, setAgencyId] = useState('');
  const [labelId, setLabelId] = useState('');
  const [status, setStatus] = useState('');
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [processing, setProcessing] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [rerunningOcrId, setRerunningOcrId] = useState<string | null>(null);
  const [rerunOcrError, setRerunOcrError] = useState('');
  const [newCategoryId, setNewCategoryId] = useState('');
  const [newAgencyId, setNewAgencyId] = useState('');
  const [newLabelIds, setNewLabelIds] = useState<string[]>([]);
  const useTaxonomyQuery = (kind: string) =>
    useQuery({
      queryKey: ['document-taxonomy', kind],
      queryFn: () =>
        api<{ data: Taxonomy[] }>(`/api/document-taxonomy/${kind}`).then((r) => r.data),
    });
  const categories = useTaxonomyQuery('categories');
  const agencies = useTaxonomyQuery('agencies');
  const labels = useTaxonomyQuery('labels');
  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (query.trim()) p.set('query', query.trim());
    if (categoryId) p.set('categoryId', categoryId);
    if (agencyId) p.set('agencyId', agencyId);
    if (labelId) p.set('labelId', labelId);
    if (status) p.set('status', status);
    return p.toString();
  }, [query, categoryId, agencyId, labelId, status]);
  const documents = useQuery({
    queryKey: ['library-documents', params],
    queryFn: () => api<{ data: DocumentItem[] }>(`/api/documents?${params}`).then((r) => r.data),
    refetchInterval: (q) =>
      q.state.data?.some((d) => ['ocr_queued', 'ocr_processing'].includes(d.status)) ? 2500 : false,
  });
  const detail = useQuery({
    queryKey: ['library-document', selectedId],
    enabled: Boolean(selectedId),
    queryFn: () => api<{ data: DocumentItem }>(`/api/documents/${selectedId}`).then((r) => r.data),
    refetchInterval: (q) =>
      ['ocr_queued', 'ocr_processing'].includes(q.state.data?.status ?? '') ? 2500 : false,
  });
  const updateQueue = (id: string, patch: Partial<QueueItem>) =>
    setQueue((items) => items.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  const chooseFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    setQueue(
      files.map((file) => ({
        id: crypto.randomUUID(),
        file,
        progress: 0,
        message: 'พร้อมอัปโหลด',
      })),
    );
    event.target.value = '';
  };

  const uploadOcrPage = async (
    documentId: string,
    pageNumber: number,
    totalPages: number,
    sourcePages: number,
    blob: Blob,
  ) => {
    const extension =
      blob.type === 'image/png' ? 'png' : blob.type === 'image/jpeg' ? 'jpg' : 'webp';
    const form = new FormData();
    form.set('page', new File([blob], `page-${pageNumber}.${extension}`, { type: blob.type }));
    form.set('totalPages', String(totalPages));
    form.set('sourcePages', String(sourcePages));
    await api(`/api/documents/${documentId}/pages/${pageNumber}`, { method: 'POST', body: form });
  };

  const rerunDocumentOcr = async (document: DocumentItem) => {
    setRerunOcrError('');
    setRerunningOcrId(document.id);
    try {
      const response = await fetch(
        apiUrl(`/api/files/${document.storedFileId}/content?inline=true`),
        { credentials: 'include' },
      );
      if (!response.ok) throw new Error('เปิดไฟล์ต้นฉบับเพื่อทำ OCR ใหม่ไม่สำเร็จ');
      const blob = await response.blob();
      const file = new File([blob], document.file.name, {
        type: document.file.contentType || blob.type,
      });
      const extension = document.file.name.split('.').at(-1)?.toLowerCase() ?? '';
      if (file.type === 'application/pdf' || extension === 'pdf') {
        const extracted = await extractPdfText(file);
        const pages = Math.min(extracted.pages, 200);
        const estimate = Number((pages * 0.06).toFixed(2));
        if (
          !window.confirm(
            `จะอ่าน OCR ใหม่จากภาพเอกสาร ${pages} หน้า\nค่าใช้จ่ายสูงสุดโดยประมาณ ฿${estimate}\nข้อความ OCR เดิมจะถูกแทนที่เมื่อประมวลผลสำเร็จ`,
          )
        )
          return;
        await renderPdfPages(
          file,
          async ({ pageNumber, totalPages, sourcePages, blob: pageBlob }) => {
            await uploadOcrPage(document.id, pageNumber, totalPages, sourcePages, pageBlob);
          },
          200,
        );
        await api(`/api/documents/${document.id}/ocr`, {
          method: 'POST',
          body: JSON.stringify({ sourcePages: extracted.pages }),
        });
      } else if (file.type.startsWith('image/')) {
        if (
          !window.confirm(
            'จะอ่าน OCR ใหม่จากภาพนี้ ค่าใช้จ่ายสูงสุดโดยประมาณ ฿0.06\nข้อความ OCR เดิมจะถูกแทนที่เมื่อประมวลผลสำเร็จ',
          )
        )
          return;
        await uploadOcrPage(document.id, 1, 1, 1, file);
        await api(`/api/documents/${document.id}/ocr`, {
          method: 'POST',
          body: JSON.stringify({ sourcePages: 1 }),
        });
      } else throw new Error('การอ่าน OCR ใหม่รองรับไฟล์ PDF และไฟล์ภาพ');
      await Promise.all([
        client.invalidateQueries({ queryKey: ['library-document', document.id] }),
        client.invalidateQueries({ queryKey: ['library-documents'] }),
      ]);
    } catch (error) {
      setRerunOcrError(error instanceof Error ? error.message : 'อ่าน OCR ใหม่ไม่สำเร็จ');
    } finally {
      setRerunningOcrId(null);
    }
  };

  const processFile = async (item: QueueItem) => {
    try {
      const uploaded = await uploadLibraryFile(item.file, (progress, message) =>
        updateQueue(item.id, { progress, message }),
      );
      let duplicateMode: 'reuse' | 'new_version' = 'new_version';
      if (uploaded.duplicate)
        duplicateMode = window.confirm(
          'พบไฟล์เดียวกันในคลัง\nกด OK เพื่อสร้างเป็นเวอร์ชันใหม่ หรือ Cancel เพื่อใช้รายการเดิม',
        )
          ? 'new_version'
          : 'reuse';
      const created = await api<{
        data: DocumentItem | { existingDocumentId: string; reused: true };
      }>('/api/documents', {
        method: 'POST',
        body: JSON.stringify({
          storedFileId: uploaded.storedFileId,
          title: baseName(item.file.name),
          categoryId: newCategoryId || null,
          agencyId: newAgencyId || null,
          labelIds: newLabelIds,
          duplicateMode,
        }),
      });
      if ('existingDocumentId' in created.data) {
        setSelectedId(created.data.existingDocumentId);
        updateQueue(item.id, { progress: 100, message: 'เปิดรายการเดิมแล้ว', done: true });
        return;
      }
      const document = created.data;
      const extension = item.file.name.split('.').at(-1)?.toLowerCase() ?? '';
      updateQueue(item.id, { progress: 96, message: 'กำลังเตรียมข้อความสำหรับค้นหา…' });
      if (item.file.type === 'application/pdf' || extension === 'pdf') {
        const extracted = await extractPdfText(item.file);
        if (
          extracted.reliableNativeText &&
          extracted.text.replace(/--- หน้า \d+ ---/g, '').trim().length >=
            Math.max(80, extracted.pages * 20)
        ) {
          await api(`/api/documents/${document.id}/ocr`, {
            method: 'POST',
            body: JSON.stringify({ nativeText: extracted.text, sourcePages: extracted.pages }),
          });
        } else {
          const pages = Math.min(extracted.pages, 200);
          const estimate = Number((pages * 0.06).toFixed(2));
          if (
            !window.confirm(
              `PDF นี้เป็นภาพ ${extracted.pages} หน้า\nจะส่ง OCR ${pages} หน้า ค่าใช้จ่ายสูงสุดโดยประมาณ ฿${estimate}\nดำเนินการต่อหรือไม่?`,
            )
          ) {
            updateQueue(item.id, {
              progress: 100,
              message: 'เก็บไฟล์แล้ว ยังไม่ได้ส่ง OCR',
              done: true,
            });
            return;
          }
          await renderPdfPages(
            item.file,
            async ({ pageNumber, totalPages, sourcePages, blob }) => {
              await uploadOcrPage(document.id, pageNumber, totalPages, sourcePages, blob);
              updateQueue(item.id, {
                progress: 96 + Math.round((pageNumber / totalPages) * 3),
                message: `เตรียม OCR หน้า ${pageNumber}/${totalPages}`,
              });
            },
            200,
          );
          await api(`/api/documents/${document.id}/ocr`, {
            method: 'POST',
            body: JSON.stringify({ sourcePages: extracted.pages }),
          });
        }
      } else if (item.file.type.startsWith('image/')) {
        if (window.confirm('ส่งภาพ 1 หน้าเข้า OCR หรือไม่? ค่าใช้จ่ายสูงสุดโดยประมาณ ฿0.06')) {
          await uploadOcrPage(document.id, 1, 1, 1, item.file);
          await api(`/api/documents/${document.id}/ocr`, {
            method: 'POST',
            body: JSON.stringify({ sourcePages: 1 }),
          });
        }
      } else if (officeTypes.includes(extension)) {
        const text = await extractOfficeText(item.file);
        if (text)
          await api(`/api/documents/${document.id}/ocr`, {
            method: 'POST',
            body: JSON.stringify({ nativeText: text, sourcePages: 1 }),
          });
      } else if (item.file.type.startsWith('text/') || ['txt', 'csv', 'md'].includes(extension)) {
        await api(`/api/documents/${document.id}/ocr`, {
          method: 'POST',
          body: JSON.stringify({
            nativeText: (await item.file.text()).slice(0, 2_000_000),
            sourcePages: 1,
          }),
        });
      }
      updateQueue(item.id, { progress: 100, message: 'นำเข้าเรียบร้อย', done: true });
      setSelectedId(document.id);
    } catch (error) {
      updateQueue(item.id, {
        error: error instanceof Error ? error.message : 'นำเข้าไม่สำเร็จ',
        message: 'นำเข้าไม่สำเร็จ',
      });
    }
  };
  const runQueue = async () => {
    setProcessing(true);
    for (const item of queue.filter((q) => !q.done)) await processFile(item);
    await client.invalidateQueries({ queryKey: ['library-documents'] });
    setProcessing(false);
  };
  const quickCreate = async (kind: 'categories' | 'agencies' | 'labels') => {
    const name = window.prompt(
      kind === 'categories'
        ? 'ชื่อประเภทเอกสาร'
        : kind === 'agencies'
          ? 'ชื่อหน่วยงาน'
          : 'ชื่อ Label',
    );
    if (!name?.trim()) return;
    await api(`/api/document-taxonomy/${kind}`, {
      method: 'POST',
      body: JSON.stringify({
        name: name.trim(),
        color: kind === 'categories' ? '#0f766e' : kind === 'agencies' ? '#0369a1' : '#7c3aed',
      }),
    });
    await client.invalidateQueries({ queryKey: ['document-taxonomy', kind] });
  };
  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/documents/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      setSelectedId(null);
      await client.invalidateQueries({ queryKey: ['library-documents'] });
    },
  });
  const askAi = async (mode: 'summary' | 'question') => {
    if (!detail.data) return;
    const prompt =
      mode === 'summary'
        ? 'สรุปสาระสำคัญ วัตถุประสงค์ ผู้เกี่ยวข้อง วันที่สำคัญ และสิ่งที่ต้องดำเนินการ'
        : window.prompt('ถามอะไรจากเอกสารนี้?');
    if (!prompt) return;
    const estimate = await api<{ data: { estimatedThb: number } }>(
      `/api/documents/${detail.data.id}/ai-estimate`,
      { method: 'POST', body: JSON.stringify({ prompt, mode }) },
    );
    if (
      !window.confirm(`ค่าใช้จ่ายโดยประมาณ ฿${estimate.data.estimatedThb}\nให้เรียก AI ต่อหรือไม่?`)
    )
      return;
    await api(`/api/documents/${detail.data.id}/ai`, {
      method: 'POST',
      body: JSON.stringify({ prompt, mode }),
    });
    await client.invalidateQueries({ queryKey: ['library-document', detail.data.id] });
  };
  const snippet = (doc: DocumentItem) => {
    if (!query.trim() || !doc.ocrText) return '';
    const index = doc.ocrText.toLowerCase().indexOf(query.toLowerCase());
    if (index < 0) return '';
    return `…${doc.ocrText.slice(Math.max(0, index - 70), index + query.length + 110).replace(/\s+/g, ' ')}…`;
  };

  return (
    <Layout title="คลังเอกสาร" wide>
      <div className="space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="workspace-eyebrow">เอกสารและความรู้</p>
            <h2 className="text-2xl font-bold text-[#edf6f3]">คลังเอกสาร</h2>
            <p className="text-sm text-[#b2c9ce]">นำเข้า จัดหมวดหมู่ และค้นหาข้อความในเอกสาร</p>
          </div>
          <Link to="/document-search" className="btn-primary">
            ⌕ สืบค้นเอกสาร
          </Link>
        </div>
        <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_420px]">
          <div className="space-y-4">
            <section className="card space-y-3">
              <div>
                <h2 className="text-lg font-bold">นำเข้าเอกสาร</h2>
                <p className="text-sm text-slate-500">
                  ไฟล์ต้นฉบับเก็บบน NAS — PDF ที่มีข้อความจะไม่เสียค่า OCR
                </p>
              </div>
              <div className="grid gap-2 sm:grid-cols-3">
                <label className="text-sm">
                  ประเภทเอกสาร
                  <div className="mt-1 flex gap-1">
                    <select
                      className="field"
                      value={newCategoryId}
                      onChange={(e) => setNewCategoryId(e.target.value)}
                    >
                      <option value="">ยังไม่ระบุ</option>
                      {categories.data?.map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.name}
                        </option>
                      ))}
                    </select>
                    <button
                      className="btn-secondary"
                      onClick={() => void quickCreate('categories')}
                    >
                      +
                    </button>
                  </div>
                </label>
                <label className="text-sm">
                  หน่วยงาน
                  <div className="mt-1 flex gap-1">
                    <select
                      className="field"
                      value={newAgencyId}
                      onChange={(e) => setNewAgencyId(e.target.value)}
                    >
                      <option value="">ยังไม่ระบุ</option>
                      {agencies.data?.map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.name}
                        </option>
                      ))}
                    </select>
                    <button className="btn-secondary" onClick={() => void quickCreate('agencies')}>
                      +
                    </button>
                  </div>
                </label>
                <div className="text-sm">
                  Label{' '}
                  <button
                    className="ml-2 font-semibold text-teal-700"
                    onClick={() => void quickCreate('labels')}
                  >
                    + สร้าง
                  </button>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {labels.data?.map((x) => (
                      <label key={x.id} className="rounded-full border px-2 py-1">
                        <input
                          type="checkbox"
                          className="mr-1"
                          checked={newLabelIds.includes(x.id)}
                          onChange={(e) =>
                            setNewLabelIds((ids) =>
                              e.target.checked ? [...ids, x.id] : ids.filter((id) => id !== x.id),
                            )
                          }
                        />
                        {x.name}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <label className="btn-secondary cursor-pointer">
                <input
                  type="file"
                  multiple
                  accept={accepted}
                  className="hidden"
                  onChange={chooseFiles}
                />
                + เลือกหลายไฟล์
              </label>
              {queue.map((item) => (
                <div key={item.id} className="rounded-xl bg-slate-50 p-3">
                  <div className="flex justify-between gap-2">
                    <strong className="truncate text-sm">{item.file.name}</strong>
                    <span className="text-xs">{(item.file.size / 1024 / 1024).toFixed(1)} MB</span>
                  </div>
                  <div className="mt-2 h-2 overflow-hidden rounded bg-slate-200">
                    <div className="h-full bg-teal-600" style={{ width: `${item.progress}%` }} />
                  </div>
                  <p className={`mt-1 text-xs ${item.error ? 'text-red-700' : 'text-slate-500'}`}>
                    {item.error ?? item.message}
                  </p>
                </div>
              ))}
              {queue.some((x) => !x.done) && (
                <button
                  className="btn-primary w-full"
                  disabled={processing}
                  onClick={() => void runQueue()}
                >
                  {processing
                    ? 'กำลังนำเข้าตามลำดับ…'
                    : `นำเข้า ${queue.filter((x) => !x.done).length} ไฟล์`}
                </button>
              )}
            </section>
            <section className="card space-y-3">
              <div className="grid gap-2 sm:grid-cols-5">
                <input
                  className="field sm:col-span-2"
                  placeholder="ค้นชื่อ เลขที่ หรือข้อความ OCR"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <select
                  className="field"
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                >
                  <option value="">ทุกประเภท</option>
                  {categories.data?.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name}
                    </option>
                  ))}
                </select>
                <select
                  className="field"
                  value={agencyId}
                  onChange={(e) => setAgencyId(e.target.value)}
                >
                  <option value="">ทุกหน่วยงาน</option>
                  {agencies.data?.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name}
                    </option>
                  ))}
                </select>
                <select
                  className="field"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="">ทุกสถานะ</option>
                  {Object.entries(statusLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <select
                className="field sm:max-w-xs"
                value={labelId}
                onChange={(e) => setLabelId(e.target.value)}
              >
                <option value="">ทุก Label</option>
                {labels.data?.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </select>
              {documents.isLoading && <p className="text-sm text-slate-500">กำลังโหลด…</p>}
              {documents.error && <ErrorNotice message={documents.error.message} />}
              <div className="divide-y">
                {documents.data?.map((doc) => (
                  <button
                    key={doc.id}
                    className="w-full py-3 text-left"
                    onClick={() => setSelectedId(doc.id)}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-semibold">{doc.title}</h3>
                        <p className="text-xs text-slate-500">
                          {doc.documentDate ?? 'ไม่ระบุวันที่'} ·{' '}
                          {doc.agency?.name ?? 'ไม่ระบุหน่วยงาน'} · รุ่น {doc.versionNumber}
                        </p>
                        {doc.legalStatus !== 'active' && (
                          <p
                            className={`mt-1 text-xs font-semibold ${doc.legalStatus === 'repealed' ? 'text-red-700' : 'text-amber-700'}`}
                          >
                            {
                              {
                                amended: 'ถูกแก้ไขเพิ่มเติม',
                                partially_repealed: 'ถูกยกเลิกบางส่วน',
                                repealed: 'ถูกยกเลิกทั้งหมด',
                              }[doc.legalStatus]
                            }
                          </p>
                        )}
                      </div>
                      <StatusPill status={doc.status} />
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {doc.category && (
                        <span
                          className="rounded-full px-2 py-0.5 text-xs"
                          style={{
                            backgroundColor: `${doc.category.color}18`,
                            color: doc.category.color,
                          }}
                        >
                          {doc.category.name}
                        </span>
                      )}
                      {doc.labels.map((x) => (
                        <span key={x.id} className="rounded-full border px-2 py-0.5 text-xs">
                          {x.name}
                        </span>
                      ))}
                    </div>
                    {snippet(doc) && <p className="mt-2 text-sm text-slate-600">{snippet(doc)}</p>}
                  </button>
                ))}
              </div>
              {documents.data?.length === 0 && (
                <p className="py-8 text-center text-sm text-slate-500">ยังไม่พบเอกสาร</p>
              )}
            </section>
          </div>
          <aside className="xl:sticky xl:top-20 xl:self-start">
            {!selectedId ? (
              <div className="card text-sm text-slate-500">
                เลือกเอกสารเพื่อดู OCR แก้หมวดหมู่ เปิดไฟล์ต้นฉบับ หรือสรุปด้วย AI
              </div>
            ) : detail.isLoading ? (
              <div className="card">กำลังโหลด…</div>
            ) : detail.data ? (
              <DocumentDetail
                key={detail.data.id}
                item={detail.data}
                searchQuery={query.trim()}
                match={documents.data?.find((document) => document.id === detail.data.id)?.match ?? null}
                categories={categories.data ?? []}
                agencies={agencies.data ?? []}
                labels={labels.data ?? []}
                onSaved={async () => {
                  await Promise.all([
                    client.invalidateQueries({ queryKey: ['library-document', selectedId] }),
                    client.invalidateQueries({ queryKey: ['library-documents'] }),
                  ]);
                }}
                onDelete={() => {
                  if (confirm('ลบรายการเอกสารนี้? ไฟล์ต้นฉบับจะยังเก็บไว้เพื่อป้องกันข้อมูลสูญหาย'))
                    remove.mutate(detail.data.id);
                }}
                onAi={(mode) => void askAi(mode)}
                onRerunOcr={() => void rerunDocumentOcr(detail.data)}
                rerunningOcr={rerunningOcrId === detail.data.id}
                rerunOcrError={rerunOcrError}
              />
            ) : (
              <ErrorNotice message={detail.error?.message ?? 'โหลดเอกสารไม่สำเร็จ'} />
            )}
          </aside>
        </div>
      </div>
    </Layout>
  );
}

function DocumentDetail({
  item,
  searchQuery,
  match,
  categories,
  agencies,
  labels,
  onSaved,
  onDelete,
  onAi,
  onRerunOcr,
  rerunningOcr,
  rerunOcrError,
}: {
  item: DocumentItem;
  searchQuery: string;
  match: DocumentItem['match'];
  categories: Taxonomy[];
  agencies: Taxonomy[];
  labels: Taxonomy[];
  onSaved: () => Promise<void>;
  onDelete: () => void;
  onAi: (mode: 'summary' | 'question') => void;
  onRerunOcr: () => void;
  rerunningOcr: boolean;
  rerunOcrError: string;
}) {
  const [title, setTitle] = useState(item.title);
  const [number, setNumber] = useState(item.documentNumber ?? '');
  const [date, setDate] = useState(item.documentDate ?? '');
  const [category, setCategory] = useState(item.category?.id ?? '');
  const [agency, setAgency] = useState(item.agency?.id ?? '');
  const [selectedLabels, setSelectedLabels] = useState(item.labels.map((x) => x.id));
  const save = useMutation({
    mutationFn: () =>
      api(`/api/documents/${item.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          title,
          documentNumber: number || null,
          documentDate: date || null,
          categoryId: category || null,
          agencyId: agency || null,
          labelIds: selectedLabels,
          status: 'ready',
        }),
      }),
    onSuccess: onSaved,
  });
  return (
    <div className="card space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xs text-slate-500">รายละเอียดเอกสาร</p>
          <h2 className="font-bold">{item.title}</h2>
          <p className="mt-1 text-xs font-semibold text-teal-700">
            สถานะทางเอกสาร:{' '}
            {
              {
                active: 'มีผลใช้งาน',
                amended: 'ถูกแก้ไข',
                partially_repealed: 'ถูกยกเลิกบางส่วน',
                repealed: 'ถูกยกเลิกทั้งหมด',
              }[item.legalStatus]
            }
          </p>
        </div>
        <StatusPill status={item.status} />
      </div>
      <input className="field" value={title} onChange={(e) => setTitle(e.target.value)} />
      <div className="grid grid-cols-2 gap-2">
        <input
          className="field"
          placeholder="เลขที่เอกสาร"
          value={number}
          onChange={(e) => setNumber(e.target.value)}
        />
        <input
          className="field"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
        <select className="field" value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">ไม่ระบุประเภท</option>
          {categories.map((x) => (
            <option key={x.id} value={x.id}>
              {x.name}
            </option>
          ))}
        </select>
        <select className="field" value={agency} onChange={(e) => setAgency(e.target.value)}>
          <option value="">ไม่ระบุหน่วยงาน</option>
          {agencies.map((x) => (
            <option key={x.id} value={x.id}>
              {x.name}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-wrap gap-1">
        {labels.map((x) => (
          <label key={x.id} className="rounded-full border px-2 py-1 text-xs">
            <input
              type="checkbox"
              className="mr-1"
              checked={selectedLabels.includes(x.id)}
              onChange={(e) =>
                setSelectedLabels((ids) =>
                  e.target.checked ? [...ids, x.id] : ids.filter((id) => id !== x.id),
                )
              }
            />
            {x.name}
          </label>
        ))}
      </div>
      <button
        className="btn-primary w-full"
        onClick={() => save.mutate()}
        disabled={save.isPending || !title.trim()}
      >
        บันทึกและยืนยันข้อมูล
      </button>
      <a
        className="btn-secondary w-full"
        href={apiUrl(`/api/files/${item.storedFileId}/content?inline=true`)}
        target="_blank"
        rel="noreferrer"
      >
        เปิดไฟล์ต้นฉบับ ({item.file.provider.toUpperCase()})
      </a>
      {(item.file.contentType === 'application/pdf' ||
        item.file.contentType.startsWith('image/') ||
        item.file.name.toLowerCase().endsWith('.pdf')) && (
        <button
          className="btn-secondary w-full"
          disabled={rerunningOcr || ['ocr_queued', 'ocr_processing'].includes(item.status)}
          onClick={onRerunOcr}
        >
          {rerunningOcr ? 'กำลังเตรียม OCR ใหม่…' : 'อ่าน OCR ใหม่จากไฟล์ต้นฉบับ'}
        </button>
      )}
      {rerunOcrError && <ErrorNotice message={rerunOcrError} />}{' '}
      {item.ocrError && <ErrorNotice message={item.ocrError} />}
      <DocumentFilesPanel documentId={item.id} files={item.files ?? []} onChanged={onSaved} searchQuery={searchQuery} matchingFileId={match?.source === 'attachment' ? match.fileId : undefined} />
      <details className="rounded-xl border p-3" open={Boolean(searchQuery && (match?.source === 'original' || item.ocrText.toLocaleLowerCase('th').includes(searchQuery.toLocaleLowerCase('th')))) || undefined}>
        <summary className="cursor-pointer font-semibold">ข้อความ OCR / ข้อความที่ค้นหาได้</summary>
        <div className="mt-3"><SearchableTextPanel key={`${item.id}-${searchQuery}`} title="ข้อความ OCR ต้นฉบับ" text={item.ocrText} initialQuery={searchQuery} /></div>
      </details>
      <DocumentRelationshipPanel documentId={item.id} />
      {item.ocrText && (
        <div className="grid grid-cols-2 gap-2">
          <button className="btn-secondary" onClick={() => onAi('summary')}>
            สรุปด้วย AI
          </button>
          <button className="btn-secondary" onClick={() => onAi('question')}>
            ถามจากเอกสาร
          </button>
        </div>
      )}
      {item.summaryText && (
        <div className="rounded-xl bg-teal-50 p-3 text-sm whitespace-pre-wrap">
          <strong>สรุป AI</strong>
          <p className="mt-2">{item.summaryText}</p>
        </div>
      )}
      <button className="w-full py-2 text-sm font-semibold text-red-700" onClick={onDelete}>
        ลบรายการเอกสาร
      </button>
    </div>
  );
}

function DocumentFilesPanel({
  documentId,
  files,
  onChanged,
  searchQuery,
  matchingFileId,
}: {
  documentId: string;
  files: DocumentFileItem[];
  onChanged: () => Promise<void>;
  searchQuery: string;
  matchingFileId?: string;
}) {
  const [pending, setPending] = useState<PendingDocumentFile[]>([]);
  const [busy, setBusy] = useState(false);
  const choose = async (event: ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files ?? []);
    event.target.value = '';
    const inspected = await Promise.all(
      selected.map(async (file): Promise<PendingDocumentFile> => {
        const extension = file.name.split('.').at(-1)?.toLowerCase() ?? '';
        try {
          if (file.type === 'application/pdf' || extension === 'pdf') {
            const extracted = await extractPdfText(file);
            return {
              id: crypto.randomUUID(),
              file,
              pages: extracted.pages,
              ocr: true,
              canOcr: true,
              estimate: extracted.reliableNativeText
                ? 0
                : Number((Math.min(extracted.pages, 200) * 0.06).toFixed(2)),
              mode: extracted.reliableNativeText ? 'native' : 'pages',
              nativeText: extracted.reliableNativeText ? extracted.text : undefined,
              status: 'พร้อมอัปโหลด',
            };
          }
          if (file.type.startsWith('image/'))
            return {
              id: crypto.randomUUID(),
              file,
              pages: 1,
              ocr: true,
              canOcr: true,
              estimate: 0.06,
              mode: 'pages',
              status: 'พร้อมอัปโหลด',
            };
          if (officeTypes.includes(extension)) {
            const text = await extractOfficeText(file);
            return {
              id: crypto.randomUUID(),
              file,
              pages: 1,
              ocr: Boolean(text),
              canOcr: Boolean(text),
              estimate: 0,
              mode: text ? 'native' : 'none',
              nativeText: text,
              status: 'พร้อมอัปโหลด',
            };
          }
          if (file.type.startsWith('text/') || ['txt', 'csv', 'md'].includes(extension)) {
            const text = (await file.text()).slice(0, 2_000_000);
            return {
              id: crypto.randomUUID(),
              file,
              pages: 1,
              ocr: true,
              canOcr: true,
              estimate: 0,
              mode: 'native',
              nativeText: text,
              status: 'พร้อมอัปโหลด',
            };
          }
        } catch {
          return {
            id: crypto.randomUUID(),
            file,
            pages: 0,
            ocr: false,
            canOcr: false,
            estimate: 0,
            mode: 'none',
            status: 'ตรวจไฟล์ไม่สำเร็จ',
          };
        }
        return {
          id: crypto.randomUUID(),
          file,
          pages: 0,
          ocr: false,
          canOcr: false,
          estimate: 0,
          mode: 'none',
          status: 'ไฟล์นี้เก็บได้ แต่ไม่รองรับ OCR',
        };
      }),
    );
    setPending((items) => [...items, ...inspected]);
  };
  const patchPending = (id: string, patch: Partial<PendingDocumentFile>) =>
    setPending((items) => items.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  const uploadPage = async (
    fileId: string,
    pageNumber: number,
    totalPages: number,
    sourcePages: number,
    blob: Blob,
  ) => {
    const extension =
      blob.type === 'image/png' ? 'png' : blob.type === 'image/jpeg' ? 'jpg' : 'webp';
    const form = new FormData();
    form.set('page', new File([blob], `page-${pageNumber}.${extension}`, { type: blob.type }));
    form.set('totalPages', String(totalPages));
    form.set('sourcePages', String(sourcePages));
    await api(`/api/documents/${documentId}/files/${fileId}/pages/${pageNumber}`, {
      method: 'POST',
      body: form,
    });
  };
  const run = async () => {
    setBusy(true);
    const completedIds: string[] = [];
    for (const item of pending.filter((x) => !x.done)) {
      try {
        patchPending(item.id, { status: 'กำลังอัปโหลด…' });
        const uploaded = await uploadLibraryFile(item.file, (_progress, message) =>
          patchPending(item.id, { status: message }),
        );
        const attached = await api<{ data: { id: string } }>(`/api/documents/${documentId}/files`, {
          method: 'POST',
          body: JSON.stringify({ storedFileId: uploaded.storedFileId }),
        });
        if (item.ocr) {
          patchPending(item.id, { status: `กำลังเตรียม OCR (${item.pages} หน้า)…` });
          if (item.mode === 'native' && item.nativeText)
            await api(`/api/documents/${documentId}/files/${attached.data.id}/ocr`, {
              method: 'POST',
              body: JSON.stringify({ nativeText: item.nativeText, sourcePages: item.pages || 1 }),
            });
          else if (item.mode === 'pages') {
            if (
              item.file.type === 'application/pdf' ||
              item.file.name.toLowerCase().endsWith('.pdf')
            )
              await renderPdfPages(
                item.file,
                async ({ pageNumber, totalPages, sourcePages, blob }) =>
                  uploadPage(attached.data.id, pageNumber, totalPages, sourcePages, blob),
                200,
              );
            else await uploadPage(attached.data.id, 1, 1, 1, item.file);
            await api(`/api/documents/${documentId}/files/${attached.data.id}/ocr`, {
              method: 'POST',
              body: JSON.stringify({ sourcePages: item.pages || 1 }),
            });
          }
        }
        completedIds.push(item.id);
      } catch (error) {
        patchPending(item.id, {
          status: 'ไม่สำเร็จ',
          error: error instanceof Error ? error.message : 'อัปโหลดไม่สำเร็จ',
        });
      }
    }
    await onChanged();
    setPending((items) => items.filter((item) => !completedIds.includes(item.id)));
    setBusy(false);
  };
  const selected = pending.filter((item) => !item.done && item.ocr);
  const totalPages = selected.reduce((sum, item) => sum + item.pages, 0);
  const totalEstimate = selected.reduce((sum, item) => sum + item.estimate, 0);
  return (
    <section className="rounded-xl border p-3 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="font-semibold">ไฟล์เพิ่มเติม</h3>
          <p className="text-xs text-slate-500">เลือก OCR แยกแต่ละไฟล์ พร้อมราคาประมาณก่อนส่ง</p>
        </div>
        <label className="btn-secondary cursor-pointer whitespace-nowrap">
          <input
            className="hidden"
            type="file"
            multiple
            accept={accepted}
            onChange={(e) => void choose(e)}
          />
          + เพิ่มไฟล์
        </label>
      </div>
      {files.map((entry) => (
        <StoredDocumentFileCard
          key={entry.id}
          documentId={documentId}
          entry={entry}
          onChanged={onChanged}
          searchQuery={searchQuery}
          matched={matchingFileId === entry.id || Boolean(searchQuery && entry.ocrText.toLocaleLowerCase('th').includes(searchQuery.toLocaleLowerCase('th')))}
        />
      ))}
      {pending.map((item) => (
        <div key={item.id} className="rounded-lg border p-2 text-sm">
          <div className="flex justify-between gap-2">
            <strong className="truncate">{item.file.name}</strong>
            <button
              className="text-red-700"
              disabled={busy}
              onClick={() => setPending((items) => items.filter((x) => x.id !== item.id))}
            >
              นำออก
            </button>
          </div>
          <p className="text-xs text-slate-500">
            {item.pages ? `${item.pages} หน้า · ` : ''}
            {(item.file.size / 1024 / 1024).toFixed(1)} MB
          </p>
          <label
            className={`mt-2 flex items-center justify-between rounded-lg p-2 ${item.canOcr ? 'bg-teal-50' : 'bg-slate-100 text-slate-500'}`}
          >
            <span>
              <input
                type="checkbox"
                className="mr-2"
                disabled={!item.canOcr || busy || item.done}
                checked={item.ocr}
                onChange={(e) => patchPending(item.id, { ocr: e.target.checked })}
              />
              ต้องการ OCR ไฟล์นี้
            </span>
            <strong>
              {item.ocr
                ? item.estimate === 0
                  ? 'ไม่เสียค่า OCR'
                  : `ประมาณ ฿${item.estimate.toFixed(2)}`
                : 'ไม่ทำ OCR'}
            </strong>
          </label>
          <p className={`mt-1 text-xs ${item.error ? 'text-red-700' : 'text-slate-500'}`}>
            {item.error ?? item.status}
          </p>
        </div>
      ))}
      {pending.some((x) => !x.done) && (
        <div className="rounded-lg bg-slate-100 p-2 text-sm">
          <div className="flex justify-between">
            <span>
              รวม OCR {selected.length} ไฟล์ · {totalPages} หน้า
            </span>
            <strong>
              {totalEstimate === 0 ? 'ไม่เสียค่า OCR' : `ประมาณ ฿${totalEstimate.toFixed(2)}`}
            </strong>
          </div>
        </div>
      )}
      {pending.some((x) => !x.done) && (
        <button className="btn-primary w-full" disabled={busy} onClick={() => void run()}>
          {busy ? 'กำลังอัปโหลดตามลำดับ…' : `อัปโหลด ${pending.filter((x) => !x.done).length} ไฟล์`}
        </button>
      )}
    </section>
  );
}

function StoredDocumentFileCard({
  documentId,
  entry,
  onChanged,
  searchQuery,
  matched,
}: {
  documentId: string;
  entry: DocumentFileItem;
  onChanged: () => Promise<void>;
  searchQuery: string;
  matched: boolean;
}) {
  const [name, setName] = useState(entry.displayName);
  const save = useMutation({
    mutationFn: (body: { displayName?: string; includeInSearch?: boolean }) =>
      api(`/api/documents/${documentId}/files/${entry.id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: onChanged,
  });
  return (
    <div className="rounded-lg bg-slate-50 p-2 text-sm space-y-2">
      <div className="flex gap-2">
        <input
          className="field min-w-0 flex-1"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <button
          className="btn-secondary"
          disabled={save.isPending || !name.trim() || name === entry.displayName}
          onClick={() => save.mutate({ displayName: name.trim() })}
        >
          บันทึกชื่อ
        </button>
      </div>
      <div className="flex items-center justify-between gap-2">
        <a
          className="font-semibold text-teal-700 truncate"
          href={apiUrl(`/api/files/${entry.storedFileId}/content?inline=true`)}
          target="_blank"
          rel="noreferrer"
        >
          เปิดไฟล์
        </a>
        <span className="text-xs">{entry.status}</span>
      </div>
      <div className="flex justify-between text-xs text-slate-500">
        <span>
          {entry.progress.sourcePages ? `${entry.progress.sourcePages} หน้า · ` : ''}
          {(entry.file.sizeBytes / 1024 / 1024).toFixed(1)} MB
        </span>
        <strong>
          {entry.estimatedCostThb == null
            ? 'ยังไม่ได้เลือก OCR'
            : entry.estimatedCostThb === 0
              ? 'OCR ไม่เสียค่าใช้จ่าย'
              : `OCR ประมาณ ฿${entry.estimatedCostThb.toFixed(2)}`}
        </strong>
      </div>
      <label className="flex items-center text-xs">
        <input
          type="checkbox"
          className="mr-2"
          checked={entry.includeInSearch}
          disabled={save.isPending}
          onChange={(e) => save.mutate({ includeInSearch: e.target.checked })}
        />
        รวมไฟล์นี้ในการค้นหาและสรุป
      </label>
      {entry.ocrError && <p className="text-xs text-red-700">{entry.ocrError}</p>}
      {entry.ocrText && (
        <details open={matched || undefined}>
          <summary className="cursor-pointer text-xs">ดูข้อความ OCR แยกไฟล์</summary>
          <div className="mt-2"><SearchableTextPanel key={`${entry.id}-${searchQuery}`} title={`ไฟล์ ${entry.displayName}`} text={entry.ocrText} initialQuery={searchQuery} compact /></div>
        </details>
      )}
      {entry.ocrRuns.length > 0 && (
        <details>
          <summary className="cursor-pointer text-xs">ประวัติ OCR ({entry.ocrRuns.length})</summary>
          <div className="mt-1 space-y-1">
            {entry.ocrRuns.map((run) => (
              <p key={run.id} className="text-xs text-slate-500">
                {new Date(run.createdAt).toLocaleString('th-TH')} · {run.provider}/{run.model} ·{' '}
                {run.sourcePages} หน้า ·{' '}
                {run.estimatedCostThb === 0
                  ? '฿0.00'
                  : `ประมาณ ฿${run.estimatedCostThb.toFixed(2)}`}{' '}
                · {run.status}
              </p>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
